import { Button, Flex, Popover, Select, Statistic } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useState } from "react";

interface BaseProps<T extends string | number | symbol> {
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    onSubmit: (value: T) => void;
    onCancel: () => void;
    value: T | undefined;
}

interface WipHighlightProps<T extends string | number | symbol> extends BaseProps<T> {
    displayValue: string | number | undefined;
}

export const WipHighlight = <T extends string | number | symbol>(props: WipHighlightProps<T>) => {
    const { t } = usePaldockTranslation("ui.report.columns");
    const { t: tC } = usePaldockTranslation("common");

    const { onCancel, ...rest } = props;
    const [open, setOpen] = useState<boolean>(true);

    const handleCancel = () => {
        onCancel();
        setOpen(false);
    };

    return (
        <Popover
            open={open}
            title={tC("add")}
            placement={"bottom"}
            content={<PopoverContent {...rest} onCancel={handleCancel} />}
        >
            <div className={"cursor-pointer w-[200px] h-[74px] rounded-lg p-2 bg-white border"}>
                <div className={"flex justify-between items-center gap-2"}>
                    <span className={"text-md text-[rgba(0,0,0,0.45)]"}>
                        {props.value ? t(String(props.value)) : "Metric"}
                    </span>
                    <div className={"w-6 h-3 rounded-md bg-primary"} />
                </div>
                <div className={"flex justify-center mt-1"}>
                    <Statistic value={props.displayValue ?? "0"} valueStyle={{ color: "var(--color-primary)" }} />
                </div>
            </div>
        </Popover>
    );
};

const PopoverContent = <T extends string | number | symbol>({
    options,
    onSubmit,
    onCancel,
    value,
    onChange,
}: BaseProps<T>) => {
    const { t } = usePaldockTranslation("common");

    const isValid = value !== undefined && value !== "";

    return (
        <Flex vertical className={"gap-4 min-w-[450px]"}>
            <Flex gap={8} vertical flex={1}>
                <span>{t("metric")}</span>
                <Select
                    placeholder={"Select metric"}
                    className={"w-full"}
                    value={value}
                    options={options}
                    onChange={onChange}
                />
            </Flex>
            <Flex flex={1} gap={8} justify={"space-between"}>
                <Button onClick={onCancel} className={"flex-1"}>
                    {t("cancel")}
                </Button>
                <Button
                    className={"flex-1"}
                    type={"primary"}
                    disabled={!isValid}
                    onClick={(e) => {
                        e.stopPropagation();

                        if (isValid) {
                            onSubmit(value);
                        }
                    }}
                >
                    {t("submit")}
                </Button>
            </Flex>
        </Flex>
    );
};
